import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';

import type { ActiveBillingCycleItem } from '../../services/api/types.gen';
import { formatMoneyDisplay } from '../../utils/currencyUtils';
import { patientHhrgWorksheetPath } from '../../utils/navigation';

/**
 * "Expected Revenue" column shared by the Billing Manager tabs that show it.
 * The amount is the billing cycle's Expected revenue as the Accounts
 * Receivable report shows it (QRP submitter): for PDGM payers the HHRG
 * estimate net of adjustments, for others the contracted per-visit rates.
 * The header tooltip says so, and the amount links to the HHRG worksheet.
 */
export const expectedRevenueColumn: ColumnDef<ActiveBillingCycleItem> = {
  id: 'expected_revenue',
  accessorKey: 'total_anticipated_payment',
  enableSorting: false,
  header: () => (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className='cursor-help'>
            Expected Revenue
          </span>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='max-w-xs text-xs'>
          Expected revenue from the Accounts Receivable report (QRP submitter).
          For PDGM payers it starts from the HHRG estimate and is net of
          adjustments such as HHVBP, primary payment and sequestration, so it
          can differ from the amount on the HHRG worksheet. For other payers it
          uses the contracted per-visit rates. Click an amount to open the
          patient&apos;s HHRG worksheet.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ),
  cell: ({ getValue, row }) => {
    const value = getValue<string | null | undefined>();
    if (!value) return '-';

    const amount = formatMoneyDisplay(value);
    const { patient_id: patientId, episode_id: episodeId } = row.original;
    if (!patientId || !episodeId) return amount;

    return (
      <Link
        to={patientHhrgWorksheetPath(patientId, episodeId)}
        className='text-primary underline-offset-4 hover:underline'
      >
        {amount}
      </Link>
    );
  },
};
